import { Button, Skeleton, Table, TableProps, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import TableCustomCell from 'components/tableCustomCell/tableCustomCell';
import AuthActionComponent from 'components/authAction/authAction';
import { PlusOutlined } from '@ant-design/icons';
import NoData from 'components/noData/noData';
import Icon from 'utils/icon';

// services
import {
	onApproveCommercialEntity,
	onDeleteCommercialEntity,
	onGetCommercialEntities,
	onRejectCommercialEntity,
	onUnlinkCommercialEntity,
} from 'modules/commercialEntities/services/commercialEntities';
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';

// models
import { CommercialEntity } from 'modules/commercialEntities/models/commercialEntities';
import { StatusColors } from 'utils/statusColors';

function ListCommercialEntitiesPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [commercialEntities, setCommercialEntities] = useState<CommercialEntity[]>([]);

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254', textAlign: 'center', display: 'block' }}>{id}</span>,
		},
		{
			title: t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITY'),
			dataIndex: 'investment_entity_name',
			key: 'investment_entity_name',
			render: (investment_entity_name) => (
				<span style={{ fontWeight: 500, color: '#3F4254' }}>{isRTL() ? investment_entity_name?.ar : investment_entity_name?.en}</span>
			),
		},
		{
			title: t('COMMERCIAL_ENTITIES.UNIFIED_CR'),
			dataIndex: 'investment_entity_unified_cr',
			key: 'investment_entity_unified_cr',
			render: (investment_entity_unified_cr) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{investment_entity_unified_cr}</span>,
		},
		{
			title: t('COMMERCIAL_ENTITIES.CR_NUMBER'),
			dataIndex: 'investment_entity_cr_number',
			key: 'investment_entity_cr_number',
			render: (investment_entity_cr_number) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{investment_entity_cr_number}</span>,
		},
		{
			title: t('COMMERCIAL_ENTITIES.GARDIAN'),
			dataIndex: 'guardian_name',
			key: 'guardian_name',
			render: (guardian_name, record) => (
				<Link to={`/users/view/${record?.guardian_id}`} style={{ textDecoration: 'none' }}>
					<TableCustomCell title={isRTL() ? guardian_name?.ar : guardian_name?.en} subTitle={record.guardian_national_id} />
				</Link>
			),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status_key',
			key: 'status',
			render: (status_key) => (
				<span className={`status ${StatusColors[status_key]} `}>{t(`COMMERCIAL_ENTITIES.${status_key?.toUpperCase()}`)}</span>
			),
		},
		{
			title: t('COMMERCIAL_ENTITIES.CR_DOCUMENT'),
			dataIndex: 'cr_document',
			key: 'cr_document',
			render: (cr_document) => (
				<Button
					type="primary"
					style={{ padding: '3px 7px' }}
					className="d-block m-auto"
					onClick={() => downloadFile(cr_document, 'cr_document.pdf')}>
					{t('COMMON.DOWNLOAD')}
				</Button>
			),
		},
		{
			title: t('COMMERCIAL_ENTITIES.APPROVED_AUTHORIZED_DOCUMENT'),
			dataIndex: 'approved_authorization_document',
			key: 'approved_authorization_document',
			render: (approved_authorization_document) => (
				<Button
					type="primary"
					style={{ padding: '3px 7px' }}
					className="d-block m-auto"
					onClick={() => downloadFile(approved_authorization_document, 'approved_authorization_document.pdf')}>
					{t('COMMON.DOWNLOAD')}
				</Button>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, commercialEntity) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="commercial_entity_view">
						<Link to={`${commercialEntity.id}`}>
							<Button type="primary" className="discard" style={{ paddingBlock: '6px' }}>
								{t('COMMON.VIEW')}
							</Button>
						</Link>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getCommercialEntities();
	}, []);

	const getCommercialEntities = async (page?: number) => {
		try {
			setIsLoading(true);

			const response = await onGetCommercialEntities(page);
			setCommercialEntities(response.data.data.investment_entities);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetCommercialEntities(page);
			setCommercialEntities(response.data.data.investment_entities);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const downloadFile = (url: string, filename: string) => {
		fetch(url)
			.then((response) => response.blob())
			.then((blob) => {
				const link = document.createElement('a');
				link.href = URL.createObjectURL(blob);
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				link.remove();
				URL.revokeObjectURL(link.href);
			})
			.catch((err) => console.error('Download failed:', err));
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITIES')}</h3>
			</div>

			<>
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5 className="d-flex align-items-center gap-2">
								{isLoading ? <Skeleton.Node active style={{ width: 40, height: 24 }} /> : totalRecords}{' '}
								{t('COMMERCIAL_ENTITIES.COMMERCIAL_ENTITIES')}
							</h5>
							<p>{t('COMMERCIAL_ENTITIES.MANAGEMENT_DESCRIPTION')}</p>
						</div>
					</header>

					{isLoading ? (
						<LoadingComponent />
					) : (
						<div className="position-relative">
							<Table
								rowKey="id"
								columns={columns}
								dataSource={commercialEntities}
								scroll={{ x: 400 }}
								pagination={{
									pageSize: 15,
									total: totalRecords,
									showSizeChanger: false,
									current: currentPage,
									onChange: fetchMoreData,
								}}
								locale={{ emptyText: <NoData /> }}
							/>
							{isLoadMore && (
								<div className="loadMoreContainer">
									<LoadingComponent />
								</div>
							)}
						</div>
					)}
				</div>
			</>
		</>
	);
}

export default ListCommercialEntitiesPage;
